import type { OrgChartData } from "./types";

export function buildChildrenMap(orgData: OrgChartData): Map<string, string[]> {
	const map = new Map<string, string[]>();
	for (const edge of orgData.edges) {
		const children = map.get(edge.source);
		if (children) {
			children.push(edge.target);
		} else {
			map.set(edge.source, [edge.target]);
		}
	}
	return map;
}

export function buildParentMap(orgData: OrgChartData): Map<string, string> {
	const map = new Map<string, string>();
	for (const edge of orgData.edges) {
		map.set(edge.target, edge.source);
	}
	return map;
}

export function computeDefaultExpanded(
	orgData: OrgChartData,
	childrenMap: Map<string, string[]>,
	expandAllByDefault: boolean
): Set<string> {
	const expanded = new Set<string>();

	if (expandAllByDefault) {
		for (const [parentId] of childrenMap) {
			expanded.add(parentId);
		}
		return expanded;
	}

	for (const rootId of orgData.roots) {
		if (childrenMap.has(rootId)) {
			expanded.add(rootId);
		}

		const levelOneChildren = childrenMap.get(rootId) ?? [];
		for (const childId of levelOneChildren) {
			if (childrenMap.has(childId)) {
				expanded.add(childId);
			}
		}
	}

	return expanded;
}

export function getAncestors(nodeId: string, parentMap: Map<string, string>): string[] {
	const ancestors: string[] = [];
	const visited = new Set<string>();
	let current = parentMap.get(nodeId);

	while (current && !visited.has(current)) {
		visited.add(current);
		ancestors.push(current);
		current = parentMap.get(current);
	}

	return ancestors;
}

export interface VisibleNode {
	id: string;
	childCount: number;
	isExpanded: boolean;
}

export interface VisibleData {
	orgData: OrgChartData;
	nodeInfo: Map<string, VisibleNode>;
}

export function computeVisibleData(
	orgData: OrgChartData,
	childrenMap: Map<string, string[]>,
	expandedIds: Set<string>
): VisibleData {
	const visibleIds = new Set<string>();
	const nodeInfo = new Map<string, VisibleNode>();
	const queue: string[] = [...orgData.roots];

	for (const id of queue) {
		visibleIds.add(id);
	}

	let head = 0;
	while (head < queue.length) {
		const nodeId = queue[head++]!;
		const children = childrenMap.get(nodeId) ?? [];
		const childCount = children.length;
		const isExpanded = expandedIds.has(nodeId);

		nodeInfo.set(nodeId, { id: nodeId, childCount, isExpanded });

		if (isExpanded) {
			for (const childId of children) {
				if (!visibleIds.has(childId)) {
					visibleIds.add(childId);
					queue.push(childId);
				}
			}
		}
	}

	for (const id of visibleIds) {
		if (!nodeInfo.has(id)) {
			const children = childrenMap.get(id) ?? [];
			nodeInfo.set(id, {
				id,
				childCount: children.length,
				isExpanded: expandedIds.has(id),
			});
		}
	}

	const employees = orgData.employees.filter((employee) => visibleIds.has(employee.id));
	const edges = orgData.edges.filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target));
	const cycles = orgData.cycles.filter((cycle) => cycle.members.every((id) => visibleIds.has(id)));
	const cycleGroups = orgData.cycleGroups.filter((group) => group.memberIds.every((id) => visibleIds.has(id)));
	const roots = orgData.roots.filter((id) => visibleIds.has(id));

	return {
		orgData: {
			employees,
			roots,
			cycles,
			cycleGroups,
			edges,
		},
		nodeInfo,
	};
}
